import { CommandPalette } from '@/components/CommandPalette';
import { ShortcutsModal } from '@/components/ShortcutsModal';
import { TopProjectPills } from '@/components/TopProjectPills';
import { HiddenInWorkspaceBanner } from '@/features/admin/components/HiddenInWorkspaceBanner';
import { PlatformSettingsApplier } from '@/features/admin/components/PlatformSettingsApplier';
import { isPlatformAdminQuery } from '@/features/admin/queries/admin';
import { FeedbackWidget } from '@/features/feedback/components/FeedbackWidget';
import { NotificationBell } from '@/features/notifications/components/NotificationBell';
import { WhatsAppStatusBanner } from '@/features/whatsapp/components/WhatsAppStatusBanner';
import { GlobalCreateWorkItemDialog } from '@/features/work-items/components/GlobalCreateWorkItemDialog';
import { MoveToProjectConfirmDialog } from '@/features/work-items/components/MoveToProjectConfirmDialog';
import { useMoveWorkItemToProject } from '@/features/work-items/hooks/useMoveWorkItemToProject';
import { WorkspaceSwitcher } from '@/features/workspaces/components/WorkspaceSwitcher';
import {
  MOVE_TO_PROJECT_EVENT,
  type MoveToProjectEventDetail,
  PROJECT_DROPPABLE_PREFIX,
  useBoardDndStore,
} from '@/lib/dnd/board-dnd-bridge';
import { useWorkspaceRole } from '@/lib/hooks/useWorkspaceRole';
import { listUnreadCountQuery } from '@/lib/supabase/queries/notifications';
import { listProjectsByWorkspaceQuery } from '@/lib/supabase/queries/projects';
import { getWorkspaceBySlugQuery } from '@/lib/supabase/queries/workspaces';
import {
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useQuery } from '@tanstack/react-query';
import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import {
  Bell,
  Building2,
  CalendarDays,
  CheckSquare,
  Clock,
  FolderKanban,
  Inbox,
  LogOut,
  MessageCircle,
  MessageSquareText,
  Plus,
  Search,
  Settings,
  Shield,
  ShieldCheck,
  User,
  UserCog,
  Users2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sidebar, SidebarLink, useSidebarExpanded } from '@/components/ui/sidebar';
import { Tooltip } from '@/components/ui/tooltip';
import { Topbar } from '@/components/ui/topbar';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useHotkey } from '@/lib/hooks/useHotkey';
import { useIdleLogout } from '@/lib/hooks/useIdleLogout';
import { supabase } from '@/lib/supabase/client';

/** Cierre de sesion automatico tras 3h sin actividad del usuario. */
const IDLE_LOGOUT_MS = 3 * 60 * 60 * 1000;

/**
 * `getSession` con un reintento ante fallo transitorio. Un 504/timeout del auth
 * de Supabase (GoTrue sin acceso a su Postgres) hacia rechazar `getSession` y,
 * sin error boundary, dejaba la pantalla en blanco (issue #182). Si el segundo
 * intento tambien falla, el error se propaga al `defaultErrorComponent` del
 * router (estado de error con reintento), nunca a un redirect erroneo ni a
 * pantalla en blanco.
 */
async function getSessionResilient() {
  try {
    return await supabase.auth.getSession();
  } catch {
    await new Promise((resolve) => setTimeout(resolve, 600));
    return await supabase.auth.getSession();
  }
}

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ location }) => {
    const { data } = await getSessionResilient();
    if (!data.session) {
      throw redirect({ to: '/login' });
    }
    // Force password change on first login
    const meta = data.session.user.user_metadata as { must_change_password?: boolean } | undefined;
    if (meta?.must_change_password === true && !location.pathname.startsWith('/change-password')) {
      throw redirect({ to: '/change-password' as never });
    }
  },
  component: AppLayout,
});

/** Evento global para abrir el modal de "crear" segun el contexto de la pagina activa. */
export const QUICK_CREATE_EVENT = 'meidei:quick-create';

function AppLayout() {
  // Sidebar: dos estados fijos (expandido / colapsado) que el usuario alterna.
  // Persistido en 'meidei.sidebar.pinned' (1=expandido, 0=colapsado). Sin key
  // (usuarios nuevos) el default es expandido.
  const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem('meidei.sidebar.pinned');
    return stored === null ? true : stored === '1';
  });
  const toggleSidebar = () => {
    setSidebarExpanded((p) => {
      const next = !p;
      try {
        window.localStorage.setItem('meidei.sidebar.pinned', next ? '1' : '0');
      } catch {
        /* noop */
      }
      return next;
    });
  };

  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [globalCreateOpen, setGlobalCreateOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState<{
    item: { id: string; title: string };
    targetProjectId: string;
  } | null>(null);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const { data: isPlatformAdmin } = useQuery(isPlatformAdminQuery());

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const wsSlug = (() => {
    const seg = pathname.split('/').filter(Boolean)[0];
    if (!seg) return undefined;
    if (['onboarding', 'login', 'accept-invite', 'legal', 'admin'].includes(seg)) return undefined;
    return seg;
  })();

  // Resolve the active workspace id so the global "Crear tarea" fallback
  // dialog knows which workspace's projects to list.
  const { data: currentWorkspace } = useQuery({
    ...getWorkspaceBySlugQuery(wsSlug ?? ''),
    enabled: !!wsSlug,
  });

  const userId = user?.id ?? '';
  const { data: unreadCount = 0 } = useQuery({
    ...listUnreadCountQuery(userId),
    enabled: !!userId,
  });

  // Configuración del workspace solo se muestra a owner/admin. Member/guest no
  // ven el link en el sidebar; la ruta también está gateada vía <RoleGate>.
  const { canManage: canManageWorkspace } = useWorkspaceRole(wsSlug);

  useHotkey('shift+?', () => setShortcutsOpen(true));
  useHotkey('mod+k', () => setCmdOpen((v) => !v));
  useHotkey('ctrl+k', () => setCmdOpen((v) => !v));
  useHotkey('[', () => toggleSidebar());
  // Alt+N → crea nueva tarea (reemplaza el "Ctrl+N" pedido: el browser
  // se queda con Ctrl+N como atajo de "nueva ventana" y no se puede
  // interceptar desde la página). Comparte la lógica del botón "+ Nuevo":
  // si una vista escucha, abre su modal; si no, abre el diálogo global.
  useHotkey('alt+n', () => handleQuickCreate());

  // Cuando una modal de crear pide "Cambiar proyecto", la cerramos del lado
  // del componente y forzamos al diálogo global para que el usuario pueda
  // re-elegir el proyecto destino. Evita el bug "creé la tarea en el
  // proyecto equivocado porque presioné + Nuevo en el board incorrecto".
  useEffect(() => {
    const onOpenGlobalCreate = () => setGlobalCreateOpen(true);
    window.addEventListener('meidei:open-global-create', onOpenGlobalCreate);
    return () => window.removeEventListener('meidei:open-global-create', onOpenGlobalCreate);
  }, []);

  // Drop on a project pill at the topbar dispatches this event from the
  // app-level DndContext. We hold the {item, targetProject} pair and open
  // the confirm dialog; the dialog's "Mover" calls the mutation.
  useEffect(() => {
    const onMove = (e: Event) => {
      const detail = (e as CustomEvent<MoveToProjectEventDetail>).detail;
      if (!detail) return;
      setPendingMove({
        item: { id: detail.itemId, title: detail.itemTitle ?? '' },
        targetProjectId: detail.targetProjectId,
      });
    };
    window.addEventListener(MOVE_TO_PROJECT_EVENT, onMove as EventListener);
    return () => window.removeEventListener(MOVE_TO_PROJECT_EVENT, onMove as EventListener);
  }, []);

  // App-level DndContext that hosts the project-pill drop targets in the
  // topbar AND any board view (project Kanban) that registers its handlers
  // via the bridge. Personal kanbans (Mi Trabajo, Equipo) keep their own
  // inner DndContext on purpose — a card from those views is not eligible
  // to be dropped on the project pills (the inner context shields it).
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const boardHandlers = useBoardDndStore((s) => s.handlers);

  // Custom collision detection: pointerWithin first so small drop targets
  // like the project pills resolve to whichever pill is under the cursor
  // (the default rectIntersection picks the largest-overlap target, which
  // for tiny pills next to a big card body lands on the wrong pill).
  // Falls back to rectIntersection so column drops on the kanban — where
  // the cursor may not strictly sit inside any column rect — still work.
  const collisionDetection: CollisionDetection = (args) => {
    const pointer = pointerWithin(args);
    if (pointer.length > 0) return pointer;
    return rectIntersection(args);
  };

  const onAppDragEnd = (e: DragEndEvent) => {
    const overId = String(e.over?.id ?? '');
    if (overId.startsWith(PROJECT_DROPPABLE_PREFIX)) {
      const targetProjectId = overId.slice(PROJECT_DROPPABLE_PREFIX.length);
      const itemId = String(e.active.id);
      const itemData = e.active.data?.current as
        | { workItem?: { id: string; title: string } }
        | undefined;
      window.dispatchEvent(
        new CustomEvent<MoveToProjectEventDetail>(MOVE_TO_PROJECT_EVENT, {
          detail: {
            itemId,
            targetProjectId,
            itemTitle: itemData?.workItem?.title,
          },
        }),
      );
      // Don't forward to the board handler — it would also try to handle the
      // drop (e.g. as a state-change). The project move pre-empts.
      return;
    }
    boardHandlers?.onDragEnd?.(e);
  };

  // Fetch projects in the active workspace once — the dialog needs the name
  // and identifier of the destination project for the confirmation copy.
  // The TopProjectPills component already fetches the same query, so this is
  // a cache hit (TanStack Query dedupes by queryKey).
  const { data: workspaceProjects = [] } = useQuery({
    ...listProjectsByWorkspaceQuery(currentWorkspace?.id ?? ''),
    enabled: !!currentWorkspace?.id,
  });

  const move = useMoveWorkItemToProject();
  const pendingTargetProject = pendingMove
    ? (workspaceProjects.find((p) => p.id === pendingMove.targetProjectId) ?? null)
    : null;

  const displayName =
    (user?.user_metadata?.display_name as string | undefined) ?? user?.email ?? 'Usuario';

  async function handleSignOut() {
    try {
      await signOut();
      toast.success('Sesion cerrada');
      await navigate({ to: '/login' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo cerrar sesion';
      toast.error(message);
    }
  }

  useIdleLogout({
    enabled: !!user,
    idleMs: IDLE_LOGOUT_MS,
    onIdle: async () => {
      try {
        await signOut();
      } catch {
        /* incluso si falla el signOut remoto, navegamos al login */
      }
      toast.info('Sesion cerrada por inactividad');
      void navigate({ to: '/login' });
    },
  });

  function handleQuickCreate() {
    // Cada vista (board, list, projects, cycles, modules) escucha y abre su modal.
    // Si la ruta actual no escucha, mostramos hint.
    let handled = false;
    const onMaybeHandled = () => {
      handled = true;
    };
    window.addEventListener(`${QUICK_CREATE_EVENT}:ack`, onMaybeHandled, { once: true });
    window.dispatchEvent(new CustomEvent(QUICK_CREATE_EVENT));
    setTimeout(() => {
      window.removeEventListener(`${QUICK_CREATE_EVENT}:ack`, onMaybeHandled);
      if (!handled) {
        // No view on this screen handles the event → open the workspace-level
        // fallback that asks the user which project the new task belongs to.
        setGlobalCreateOpen(true);
      }
    }, 50);
  }

  return (
    <DndContext
      sensors={dndSensors}
      collisionDetection={collisionDetection}
      onDragStart={(e) => boardHandlers?.onDragStart?.(e)}
      onDragEnd={onAppDragEnd}
    >
      <PlatformSettingsApplier />
      <div className="min-h-screen flex bg-surface-canvas">
        <ShortcutsModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
        <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
        {/* Floating feedback widget — visible en todas las rutas autenticadas. */}
        <FeedbackWidget />
        {currentWorkspace?.id && (
          <GlobalCreateWorkItemDialog
            open={globalCreateOpen}
            onOpenChange={setGlobalCreateOpen}
            workspaceId={currentWorkspace.id}
          />
        )}
        <MoveToProjectConfirmDialog
          open={!!pendingMove && !!pendingTargetProject}
          onOpenChange={(v) => {
            if (!v) setPendingMove(null);
          }}
          workItem={pendingMove?.item ?? null}
          targetProject={
            pendingTargetProject
              ? {
                  id: pendingTargetProject.id,
                  name: pendingTargetProject.name,
                  identifier: pendingTargetProject.identifier ?? null,
                }
              : null
          }
          loading={move.isPending}
          onConfirm={() => {
            if (!pendingMove || !pendingTargetProject) return;
            move.mutate(
              {
                workItemId: pendingMove.item.id,
                targetProjectId: pendingTargetProject.id,
                targetProjectName: pendingTargetProject.name,
                itemTitle: pendingMove.item.title,
              },
              { onSettled: () => setPendingMove(null) },
            );
          }}
        />
        <Sidebar
          expanded={sidebarExpanded}
          onToggle={toggleSidebar}
          header={
            <div className="flex items-center min-w-0 w-full">
              {/* El WorkspaceSwitcher es el unico elemento del header: muestra el
                ws activo, el indicador "Admin" en /admin/*, o el fallback en
                onboarding. Antes habia tambien un cuadrito de marca cuando no
                habia ws activo, pero duplicaba el badge y reforzaba el look
                "vacio/roto" (sobre todo colapsado y en admin). */}
              <WorkspaceSwitcher currentSlug={wsSlug} />
            </div>
          }
        >
          {wsSlug ? (
            <div className="flex flex-col gap-0.5">
              <SidebarLink
                to={`/${wsSlug}/inbox`}
                label="Inbox"
                icon={Inbox}
                badgeCount={unreadCount}
              />
              <SidebarLink to={`/${wsSlug}/my-work`} label="Mi trabajo" icon={CheckSquare} />
              <SidebarLink to={`/${wsSlug}/projects`} label="Proyectos" icon={FolderKanban} />
              <SidebarLink to={`/${wsSlug}/calendar`} label="Calendario" icon={CalendarDays} />
              <SidebarLink to={`/${wsSlug}/time`} label="Tiempo" icon={Clock} />
              <SidebarLink to={`/${wsSlug}/users`} label="Equipo" icon={Users2} />
              <SidebarLink to={`/${wsSlug}/settings/whatsapp`} label="Notificaciones" icon={Bell} />
              {canManageWorkspace && (
                <SidebarLink
                  to={`/${wsSlug}/settings/general`}
                  label="Configuracion"
                  icon={Settings}
                />
              )}
              <SidebarLink to={`/${wsSlug}/profile`} label="Mi perfil" icon={User} />
            </div>
          ) : (
            <SidebarPlaceholderText text="Selecciona un workspace para navegar." />
          )}

          {isPlatformAdmin ? (
            <div className="flex flex-col gap-0.5 mt-2 pt-2 border-t border-border-soft">
              <SidebarSectionLabel icon={Shield} label="Admin" />
              <SidebarLink to="/admin/workspaces" label="Workspaces" icon={Building2} />
              <SidebarLink to="/admin/users" label="Usuarios" icon={UserCog} />
              <SidebarLink to="/admin/roles" label="Roles y permisos" icon={ShieldCheck} />
              <SidebarLink to="/admin/whatsapp" label="WhatsApp" icon={MessageCircle} />
              <SidebarLink to="/admin/feedback" label="Feedback" icon={MessageSquareText} />
            </div>
          ) : null}
        </Sidebar>

        <main className="flex-1 min-w-0 flex flex-col">
          <WhatsAppStatusBanner />
          <Topbar
            breadcrumbs={<TopProjectPills wsSlug={wsSlug} />}
            search={
              <div className="relative w-full">
                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-mute pointer-events-none"
                />
                <Input
                  type="search"
                  readOnly
                  placeholder="Buscar... (Ctrl+K)"
                  className="pl-8 h-8 text-xs w-full cursor-pointer"
                  onFocus={() => setCmdOpen(true)}
                  onClick={() => setCmdOpen(true)}
                />
              </div>
            }
            actions={
              wsSlug ? (
                <Tooltip content="Crear work item (C)" side="bottom">
                  <Button
                    type="button"
                    size="sm"
                    variant="default"
                    onClick={handleQuickCreate}
                    className="h-8 gap-1.5"
                  >
                    <Plus size={14} />
                    Nuevo
                  </Button>
                </Tooltip>
              ) : null
            }
            right={
              <>
                <HiddenInWorkspaceBanner wsSlug={wsSlug} />
                <NotificationBell />
                <AvatarMenu displayName={displayName} wsSlug={wsSlug} onSignOut={handleSignOut} />
              </>
            }
          />

          <div className="flex-1 min-h-0">
            <Outlet />
          </div>
        </main>
      </div>
    </DndContext>
  );
}

function AvatarMenu({
  displayName,
  wsSlug,
  onSignOut,
}: {
  displayName: string;
  wsSlug: string | undefined;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        className="rounded-full focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-sf)]"
        aria-label="Menu de usuario"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar name={displayName} size="sm" />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-48 rounded-md border border-border bg-surface shadow-pop p-1 z-40"
        >
          <div className="px-2 py-1.5 text-xs text-quiet truncate">{displayName}</div>
          <div className="h-px bg-border-soft my-1" />
          {wsSlug ? (
            <Link
              to={'/$wsSlug/profile' as never}
              params={{ wsSlug } as never}
              onMouseDown={(e) => e.preventDefault()}
              className="w-full h-8 rounded-md px-2 text-sm text-body hover:bg-accent-sf hover:text-accent inline-flex items-center gap-2"
              role="menuitem"
            >
              <User size={14} />
              <span>Mi perfil</span>
            </Link>
          ) : null}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onSignOut}
            className="w-full h-8 rounded-md px-2 text-sm text-body hover:bg-accent-sf hover:text-accent inline-flex items-center gap-2"
            role="menuitem"
          >
            <LogOut size={14} />
            <span>Cerrar sesion</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

// Texto placeholder dentro del sidebar — solo se muestra cuando está expandido.
// Cuando colapsa (56px), las palabras se rompían en columna y se veían mal.
function SidebarPlaceholderText({ text }: { text: string }) {
  const expanded = useSidebarExpanded();
  if (!expanded) return null;
  return <div className="px-2 py-3 text-xs text-quiet leading-snug">{text}</div>;
}

// Header de sección del sidebar (ej. "Admin"). Cuando está colapsado mostramos
// solo el ícono centrado; expandido muestra ícono + label en uppercase.
function SidebarSectionLabel({ icon: Icon, label }: { icon: typeof Shield; label: string }) {
  const expanded = useSidebarExpanded();
  if (!expanded) {
    return (
      <Tooltip content={label} side="right">
        <div className="h-6 flex items-center justify-center text-mute" aria-label={label}>
          <Icon size={12} />
        </div>
      </Tooltip>
    );
  }
  return (
    <div className="px-2 pb-0.5">
      <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-mute">
        <Icon size={11} />
        {label}
      </span>
    </div>
  );
}
